import React, { useEffect, useRef, useState } from "react";
import "./Hero.css";
import { FaArrowRight, FaGithub, FaLinkedin, FaReact, FaJava } from "react-icons/fa";

import heroJpg from "./assets/hero-girl.jpg";
import heroWebp from "./assets/hero-girl.webp";
import { GITHUB, LINKEDIN } from "./siteInfo";

const words = ["interfaces", "experiences", "products", "components"];

const marquee = [
  "React.js", "JavaScript", "Redux", "HTML5", "CSS3", "Responsive Design",
  "REST APIs", "Spring Boot", "Java", "MySQL", "Git",
];

function useRotatingWord(list, ms = 2200) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setI((n) => (n + 1) % list.length), ms);
    return () => clearInterval(t);
  }, [list, ms]);
  return [list[i], i];
}

/* Mouse-driven 3D tilt: the stage rotates and each layer shifts by its own depth */
function useStageTilt() {
  const ref = useRef(null);
  const set = (x, y) => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--ry", `${x * 10}deg`);
    el.style.setProperty("--rx", `${-y * 8}deg`);
    el.style.setProperty("--mx", x.toFixed(3));
    el.style.setProperty("--my", y.toFixed(3));
  };
  return {
    ref,
    onMove: (e) => {
      const r = ref.current.getBoundingClientRect();
      set((e.clientX - r.left) / r.width - 0.5, (e.clientY - r.top) / r.height - 0.5);
    },
    onLeave: () => set(0, 0),
  };
}

const Hero = () => {
  const [word, i] = useRotatingWord(words);
  const stage = useRef(null);
  const tilt = useStageTilt();

  // as the page scrolls, the hero tilts back in 3D and recedes
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const update = () => {
      const p = Math.min(window.scrollY / (window.innerHeight * 0.9), 1);
      if (stage.current) {
        stage.current.style.transform = `perspective(1200px) translateY(${p * 80}px) rotateX(${p * 14}deg) scale(${1 - p * 0.1})`;
        stage.current.style.opacity = String(1 - p * 0.75);
      }
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="hero" id="home">
      <div className="hero-bg" />

      <div className="container hero-inner" ref={stage}>
        <div className="hero-left">
          <span className="hero-badge reveal">
            <i /> React Developer · Open to work
          </span>

          <h1 className="reveal" style={{ "--d": "80ms" }}>
            Kiran <em>Sable</em>
            <small className="h1-sub">React Developer · Pune, India</small>
          </h1>

          <h2 className="reveal" style={{ "--d": "140ms" }}>
            I craft fast, delightful
            <span className="rotator">
              <em key={i}>{word}</em>
            </span>
            with React.
          </h2>

          <p className="description reveal" style={{ "--d": "220ms" }}>
            A frontend-focused developer who turns designs into smooth,
            accessible and responsive web apps, and connects them to solid
            Java Spring Boot backends when a project needs the full stack.
          </p>

          <div className="hero-buttons reveal" style={{ "--d": "300ms" }}>
            <a href="#projects" className="btn btn-light">
              View my work <FaArrowRight />
            </a>
            <a href="#contact" className="btn btn-outline">
              Hire me
            </a>
            <div className="hero-social">
              <a href={GITHUB} target="_blank" rel="me noopener noreferrer" aria-label="GitHub">
                <FaGithub />
              </a>
              <a href={LINKEDIN} target="_blank" rel="me noopener noreferrer" aria-label="LinkedIn">
                <FaLinkedin />
              </a>
            </div>
          </div>
        </div>

        <div className="hero-right reveal" style={{ "--d": "200ms" }}>
          <div className="stage" ref={tilt.ref} onMouseMove={tilt.onMove} onMouseLeave={tilt.onLeave}>
            <div className="stage-glow" />
            <div className="orbit o1"><i /></div>
            <div className="orbit o2"><i /></div>
            <div className="portrait-frame">
              <picture>
                <source srcSet={heroWebp} type="image/webp" />
                <img
                  className="portrait-img"
                  src={heroJpg}
                  alt="Illustration of Kiran Sable smiling at her work desk"
                  width="880"
                  height="1173"
                  fetchpriority="high"
                  decoding="async"
                />
              </picture>
              <span className="lamp-glow" />
              <span className="shine" />
            </div>
            <span className="fx fx-heart h1">&#9829;</span>
            <span className="fx fx-heart h2">&#9829;</span>
            <span className="fx fx-heart h3">&#9829;</span>
            <span className="fx fx-spark s1">&#10022;</span>
            <span className="fx fx-spark s2">&#10022;</span>
            <span className="fx fx-spark s3">&#10022;</span>

            <div className="chip code-card">
              <div className="dots"><b /><b /><b /></div>
              <code><span className="k">const</span> <span className="f">Portfolio</span> = () =&gt; (</code>
              <code>&nbsp;&nbsp;&lt;<span className="t">Hero</span> <span className="a">name</span>=<span className="s">"Kiran"</span> /&gt;</code>
              <code>)<span className="caret" /></code>
            </div>
            <div className="chip c-react"><FaReact /> React.js</div>
            <div className="chip c-java"><FaJava /> Spring Boot</div>
            <div className="chip c-stat"><b>2+</b> years building</div>
          </div>
        </div>
      </div>

      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[...marquee, ...marquee].map((m, n) => (
            <span key={n}>{m}<b>✦</b></span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
